/**
 * `entities/brief-template` query options factory.
 *
 * `briefTemplatesQueryOptions` is a thin TanStack `queryOptions` factory
 * wrapping `BriefTemplatesService.list` from the assets-api SDK. Consumers
 * compose it with whichever observer fits the call site: `useQuery`,
 * `useSuspenseQuery`, `queryClient.prefetchQuery`, route loaders, etc.
 *
 * Notes:
 *   - List endpoint has NO pagination/sort; it filters by `status`, `level`
 *     and `enabled`.
 *   - `assetsHttp` auto-injects the `x-selected-workspace-id` header, so the
 *     workspace context is implicit at call sites.
 *   - The `if (!response.data)` check is a defensive guard for the
 *     pathological 200-with-empty-body case (mirrors `entities/brief`).
 *     Note: `data: []` is valid (empty list) and passes the guard untouched.
 */
import {
  BriefTemplatesService,
  type WorkspaceResolutionResponseDto,
} from '@inference.engine/assets-api-client/web';
import { queryOptions } from '@tanstack/react-query';
import { assetsHttp } from '@/lib/api/http-client';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';
import { BRIEF_TEMPLATES_QUERY_KEYS, type BriefTemplateListFilters } from './query-keys';

export const briefTemplatesQueryOptions = (filters: BriefTemplateListFilters = {}) =>
  queryOptions({
    queryKey: BRIEF_TEMPLATES_QUERY_KEYS.list(filters),
    queryFn: async ({ signal }) => {
      const response = await BriefTemplatesService.list({
        client: assetsHttp,
        signal,
        query: {
          ...(filters.status && { status: filters.status }),
          ...(filters.level && { level: filters.level }),
          ...(filters.enabled !== undefined && { enabled: filters.enabled }),
        },
      });

      if (!response.data) {
        throw new Error('Failed to list brief templates');
      }

      return response.data;
    },
  });

type BriefTemplateDetailOptions = {
  suppressErrorNotification?: boolean;
};

export const briefTemplateDetailQueryOptions = (id: string, options?: BriefTemplateDetailOptions) =>
  queryOptions({
    // `options` only tunes toast suppression on the request — the fetched data
    // is identical, so it must NOT split the cache bucket.
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: BRIEF_TEMPLATES_QUERY_KEYS.detail(id),
    queryFn: async ({ signal }) => {
      const response = await BriefTemplatesService.getById({
        client: assetsHttp,
        path: { id },
        signal,
        // When the resolution seam is active it owns the not-found UX, so a
        // cross-workspace 404 must not flash the global toast. Opt-in only:
        // every other consumer keeps the pre-seam toast behavior.
        headers: options?.suppressErrorNotification
          ? // eslint-disable-next-line i18next/no-literal-string -- HTTP header value
            { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' }
          : undefined,
      });

      if (!response.data) {
        throw new Error('Failed to load brief template');
      }

      return response.data;
    },
  });

const resolveBriefTemplate = async (id: string): Promise<WorkspaceResolutionResponseDto> => {
  const response = await BriefTemplatesService.resolveWorkspace({
    client: assetsHttp,
    path: { id },
    // The resolution seam maps a 404 to its own generic "not available"
    // state; the global toast would leak the raw error first.
    // eslint-disable-next-line i18next/no-literal-string -- HTTP header value
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
  });

  if (!response.data) {
    throw new Error('Failed to resolve brief template');
  }

  return response.data;
};

/**
 * Deep-link resolution of a brief template id to its owning workspace.
 * Runs BEFORE the workspace-scoped detail query (via `useDeepLinkResolution`)
 * so a deep link from another workspace doesn't 404 under the ambient
 * `x-selected-workspace-id` header.
 */
export const briefTemplateResolutionQuery = (id?: string) =>
  queryOptions({
    queryKey: BRIEF_TEMPLATES_QUERY_KEYS.resolution(id ?? ''),
    queryFn: () => resolveBriefTemplate(id!),
  });
